import { ExpandMore } from '@mui/icons-material';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Grid,
	List,
	ListItem,
	ListItemText,
	Theme,
	Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useMutation } from '@parkchirp/api-client';
import { FC, useCallback } from 'react';

import { DELETE_SEQUENCING_TIER } from '../../../mutations';
import { SelectedParker, SequencingTier, SetShowDialog, SetShowSnackbar } from '../../../types';
import { DeleteBaseTierModal } from './deleteBaseTierModal';
import { SequencingParkerListItem } from './sequencingParkerListItem';

type BaseTierProps = {
	theme: Theme;
	tiers: SequencingTier[];
	inBulkSequencingMode: boolean;
	maxSequenceNumber: number;
	addSelectedParker: ({ id, sequenceNumber }: SelectedParker) => void;
	removeSelectedParker: (parkerId: string) => void;
	setShowSnackbar: SetShowSnackbar;
	setShowDialog: SetShowDialog;
	handleWaitForResequencing: () => Promise<void>;
};

export const BaseTierList: FC<BaseTierProps> = ({
	theme,
	tiers,
	inBulkSequencingMode,
	maxSequenceNumber,
	addSelectedParker,
	removeSelectedParker,
	handleWaitForResequencing,
	setShowSnackbar,
	setShowDialog,
}) => {
	const [deleteSequencingTier, { loading: deleteInProgress }] = useMutation(DELETE_SEQUENCING_TIER);

	const handleDelete = useCallback(
		async (tierId: number) => {
			try {
				setShowDialog(null);
				await deleteSequencingTier({
					variables: { id: tierId },
					context: { apiVersion: 'v2' },
					onCompleted: async () => {
						setShowSnackbar({
							message: 'Rate/sublot successfully removed from base tier',
							severity: 'success',
							autoHideDuration: 3000,
						});

						await handleWaitForResequencing();
					},
				});
			} catch {
				setShowSnackbar({
					message: 'Failed to remove rate/sublot combination from base tier',
					severity: 'error',
					autoHideDuration: 5000,
				});
			}
		},
		[deleteSequencingTier, handleWaitForResequencing, setShowSnackbar, setShowDialog],
	);

	return (
		<Accordion defaultExpanded sx={{ width: '100%' }}>
			<AccordionSummary expandIcon={<ExpandMore />}>
				<Typography component='span' variant='h6' fontWeight={600}>
					Base Tier
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<Grid container sx={{ display: 'flex', justifyContent: 'left' }}>
					<Button
						variant='contained'
						size='small'
						sx={{ marginBottom: '20px' }}
						onClick={() => {
							setShowDialog(
								<DeleteBaseTierModal
									theme={theme}
									tiers={tiers}
									setShowSnackbar={setShowSnackbar}
									handleClose={() => setShowDialog(null)}
									handleDelete={handleDelete}
								/>,
							);
						}}
					>
						{deleteInProgress ? (
							<CircularProgress style={{ height: '20px', width: '20px' }} />
						) : (
							<span>Remove</span>
						)}
					</Button>
				</Grid>
				<List
					sx={{
						width: '100%',
						bgcolor: 'background.paper',
						maxHeight: '450px',
						overflow: 'auto',
					}}
				>
					{tiers.map((tier) => (
						<Box key={`box-id-${tier.id}`} mb={3}>
							<Divider>
								<Chip label={`${tier.rateName}/${tier.sublotName}`} size='medium' />
							</Divider>
							{tier.parkers.length === 0 && (
								<ListItem sx={{ width: '100%' }} alignItems='center'>
									<ListItemText
										primary='No Parkers Assigned'
										sx={{ display: 'flex', justifyContent: 'center' }}
									/>
								</ListItem>
							)}
							{tier.parkers.length > 0 && (
								<ListItem
									sx={{ width: '100%' }}
									alignItems='flex-start'
									secondaryAction={<Typography variant='body1'>Sequence Number</Typography>}
								>
									<ListItemText primary='Parker' />
								</ListItem>
							)}
							{tier.parkers.map((parker) => (
								<>
									<Divider key={`parker-divider-${parker.id}`} />
									<SequencingParkerListItem
										key={parker.id}
										parker={parker}
										inBulkSequencingMode={inBulkSequencingMode}
										maxSequenceNumber={maxSequenceNumber}
										disabled={false}
										setShowSnackbar={setShowSnackbar}
										addSelectedParker={addSelectedParker}
										removeSelectedParker={removeSelectedParker}
										handleWaitForResequencing={handleWaitForResequencing}
									/>
								</>
							))}
						</Box>
					))}
				</List>
			</AccordionDetails>
		</Accordion>
	);
};
